import React, { useState } from 'react';
import { Box, Typography, Stack, IconButton, Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { staggerContainer, staggerItem } from '../motion/presets';



const fontStack = `'Fira Mono', 'Menlo', 'Consolas', 'Liberation Mono', 'monospace'`;

const Contact = () => {
  const [copied, setCopied] = useState('');
  const { i18n } = useTranslation();

  const CONTACT_INFO = {
    name: i18n.t('contact.name'),
    phone: i18n.t('contact.phone'),
    email: i18n.t('contact.email'),
  };


  const handleCopy = async (value, type) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(type);
      setTimeout(() => setCopied(''), 1200);
    } catch {
      setCopied('');
    }
  };

  return (
    <Box
      component="footer"
      id="contact"
      sx={{
        width: '100%',
        alignSelf: 'stretch',
        mt: { xs: 6, sm: 10 },
        boxSizing: 'border-box',
        background: 'rgba(30, 30, 34, 0.98)',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.28)',
      }}
    >
      <Box
        sx={{
          height: 3,
          width: '100%',
          background: 'linear-gradient(90deg, #77216f 60%, #e95420 100%)',
        }}
      />

      <Box
        sx={{
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          px: { xs: 2, sm: 4, md: 8, lg: 12 },
          py: { xs: 3, sm: 4 },
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          fontFamily: 'Ubuntu, Montserrat, sans-serif',
        }}
      >
        <Box
          component={motion.h2}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          sx={{
            m: 0,
            fontWeight: 700,
            textAlign: 'center',
            color: '#fff',
            mb: { xs: 2, sm: 2.5 },
            letterSpacing: 0.5,
            fontSize: { xs: 18, sm: 22 },
          }}
        >
          {i18n.t('contact.title')}
        </Box>

        <Stack
          component={motion.div}
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          spacing={{ xs: 2, md: 0 }}
          sx={{
            width: '100%',
            maxWidth: 1200,
            mb: 2,
            flexDirection: 'column',
            alignItems: 'center',
            '@media (min-width: 770px)': {
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 4,
            },
          }}
        >
          <Stack
            component={motion.div}
            variants={staggerItem}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              width: { xs: '100%', md: 'auto' },
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <PersonIcon sx={{ color: '#f6c915', fontSize: 22 }} />
            <Typography
              fontWeight={600}
              sx={{
                color: '#fff',
                fontSize: { xs: 15, sm: 16 },
                fontFamily: fontStack,
                textAlign: 'center',
              }}
            >
              {CONTACT_INFO.name}
            </Typography>
          </Stack>

          <Stack
            component={motion.div}
            variants={staggerItem}
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              width: { xs: '100%', md: 'auto' },
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <PhoneIcon sx={{ color: '#25d366', fontSize: 20 }} />
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontSize: { xs: 14, sm: 15 },
                fontWeight: 500,
                fontFamily: fontStack,
                wordBreak: 'break-word',
                textAlign: 'center',
              }}
            >
              {CONTACT_INFO.phone}
            </Typography>
            <Tooltip title={copied === 'phone' ? 'Copied!' : 'Copy'} placement="top">
              <IconButton
                onClick={() => handleCopy(CONTACT_INFO.phone, 'phone')}
                size="small"
                aria-label="Copy phone"
                sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#e95420' } }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack
            component={motion.div}
            variants={staggerItem}
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              width: { xs: '100%', md: 'auto' },
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <EmailIcon sx={{ color: '#61afef', fontSize: 20 }} />
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontSize: { xs: 13, sm: 14 },
                fontWeight: 500,
                fontFamily: fontStack,
                wordBreak: 'break-word',
                textAlign: 'center',
                maxWidth: { xs: 'min(100%, 280px)', md: 'none' },
              }}
            >
              {CONTACT_INFO.email}
            </Typography>
            <Tooltip title={copied === 'email' ? 'Copied!' : 'Copy'} placement="top">
              <IconButton
                onClick={() => handleCopy(CONTACT_INFO.email, 'email')}
                size="small"
                aria-label="Copy email"
                sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#e95420' } }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Typography
          variant="body2"
          align="center"
          sx={{
            color: 'rgba(255,255,255,0.45)',
            fontSize: { xs: 11, sm: 13 },
            fontFamily: fontStack,
            px: 1,
          }}
        >
          &copy; {new Date().getFullYear()} Abdulaziz Almuflehi. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default Contact;
